/**
 * ProductionEngine - Handles clip production logic
 * 
 * Strategy: Strangler Pattern
 * - GameState delegates production calculations to this engine
 * - Engine mutates GameState directly (preserves exact behavior)
 * - makeClips() stays on GameState (canonical operation)
 * 
 * CRITICAL: Preserve exact calculation order for golden-master tests
 */

import type { GameState } from '../GameState';

export class ProductionEngine {
  /**
   * Fast tick production - runs every 10ms
   * Handles AutoClippers, MegaClippers, and Factories
   * 
   * IMPORTANT: Code copied verbatim from GameState.fastTick() lines 304-340
   * Do NOT refactor, reorder, or optimize - order matters for determinism
   */
  runFastProduction(gs: GameState, tickMult: number): void {
    // AutoClipper production
    if (gs.clipmakerLevel > 0 && gs.wire >= 1) {
      const autoClips = gs.clipperBoost * (gs.clipmakerLevel / 100) * tickMult;
      gs.makeClips(autoClips);
    }

    // MegaClipper production
    if (gs.megaClipperLevel > 0 && gs.wire >= 1) {
      const megaClips = gs.megaClipperBoost * (gs.megaClipperLevel * 5) * tickMult;
      gs.makeClips(megaClips);
    }

    // Factory production (Phase 2+)
    // Factories consume power when in space phase
    if (gs.factoryLevel > 0) {
      const powerPerFactory = 10; // Power consumed per factory per tick
      const requiredPower = gs.factoryLevel * powerPerFactory * tickMult;

      // Calculate efficiency based on available power (1.0 if enough power, less if not)
      let powerEfficiency = 1;
      if (gs.spaceFlag && gs.currentPower < requiredPower) {
        powerEfficiency = gs.currentPower > 0 ? gs.currentPower / requiredPower : 0;
      }

      // Consume power in space phase
      if (gs.spaceFlag) {
        gs.currentPower = Math.max(0, gs.currentPower - requiredPower * powerEfficiency);
      }

      const factoryClips = gs.powMod * gs.factoryBoost * (gs.factoryLevel * gs.factoryRate) * powerEfficiency * tickMult;
      gs.makeClips(factoryClips);
    }
  }
}
